import { useNavigate } from 'react-router-dom'
import {
  getQuoteItemCount,
  getQuoteMessageCount,
  type QuoteListRow,
} from 'src/api/db/apiQuote'
import { buildAdminOrderPath } from 'src/routes/paths'
import { Row } from 'src/shared/components/Semantic/all'
import { Badge } from 'src/shared/components/ui/badge'
import {
  TableCell,
  TableRow,
} from 'src/shared/components/ui/table'
import { getQuoteStatusOption } from 'src/shared/consts/quoteStatus'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { formatDate as formatDateUtil } from 'src/shared/utils'
import { Box, Building, MessageCircle } from 'lucide-react'

export const RequestOverviewRow = ({ quote }: { quote: QuoteListRow }) => {
  const navigate = useNavigate()
  const { locale, t } = useLocalization()
  const itemCount = getQuoteItemCount(quote)
  const messageCount = getQuoteMessageCount(quote)

  return (
    <TableRow
      className="cursor-pointer"
      onClick={() => navigate(buildAdminOrderPath(quote.public_id ?? ''))}
    >
      <TableCell className="max-w-0 py-3">
        <ColumnMeta
          title={quote.name || t('Untitled request', 'Namnlös förfrågan')}
          subtitle={quote.public_id || '—'}
        />
      </TableCell>

      <TableCell className="max-w-0 py-3">
        <Row className="min-w-0 items-center gap-2 text-sm text-muted-foreground">
          <Building className="h-4 w-4 shrink-0" />
          <span className="truncate">{quote.organization?.name || '—'}</span>
        </Row>
      </TableCell>

      <TableCell className="w-[9.5rem] py-3">
        <Badge
          size="sm"
          variant={getQuoteStatusOption(quote.status)?.color}
          className="shrink-0"
        >
          {t(
            getQuoteStatusOption(quote.status)?.label ??
              'Draft || Utkast'
          )}
        </Badge>
      </TableCell>

      <TableCell className="w-[5.5rem] py-3">
        <Row className="items-center gap-3 text-sm text-muted-foreground">
          <Row className="items-center gap-1" title={t('Items', 'Artiklar')}>
            <Box className="h-3.5 w-3.5" />
            <span className="tabular-nums">{itemCount}</span>
          </Row>
          <Row
            className="items-center gap-1"
            title={t('Messages', 'Meddelanden')}
          >
            <MessageCircle className="h-3.5 w-3.5" />
            <span className="tabular-nums">{messageCount}</span>
          </Row>
        </Row>
      </TableCell>

      <TableCell className="w-[7.5rem] py-3 text-right text-sm text-muted-foreground tabular-nums">
        {formatDateUtil(quote.created_at, locale)}
      </TableCell>
    </TableRow>
  )
}

const ColumnMeta = ({
  title,
  subtitle,
}: {
  title: string
  subtitle: string
}) => (
  <div className="min-w-0">
    <p className="truncate text-sm font-medium">{title}</p>
    <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
  </div>
)
